import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { communesService, communesAssocieesDelegueesService, transfertsService } from "services";
import { TypeCommuneAssocieeDeleguee } from "models";
import type { CommuneAssocieeDeleguee, Commune, Transfert } from "models";
import LoadingScreen from "components/Template/LoadingScreen";
import { Container, Grid, Typography, useMediaQuery } from "@mui/material";
import PageTitle from "components/PageTitle";
import CommuneLieesSubtitle from "components/CommuneLieesSubtitle";
import InformationBox from "components/InformationBox";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { dateUtil } from "@sdeapps/react-core";
import { useErrorHandler, withPageErrorBoundary } from "utils/errorHandling";
import { CommuneIcon } from "icons";
import PopulationSpan from "components/Span/PopulationSpan";
import { useRequiredParams } from "hooks";
import type Competence from "constants/Competence";
import { competencesUtil } from "utils";
import { ContactsMetiersInformationBox } from "components/Informations/ContactsMetiers/ContactsMetiersInformationBox";

function CommuneAssocieeDelegueePage(): ReactNode {
  const { id } = useRequiredParams<{ id: string }>();
  const [commune, setCommune] = useState<CommuneAssocieeDeleguee>();
  const [chefLieu, setChefLieu] = useState<Commune>();
  const [transferts, setTransferts] = useState<Array<Transfert>>([]);
  const [competences, setCompetences] = useState<Array<Competence>>([]);

  const { catchErrors } = useErrorHandler();

  useEffect(() => {
    async function fetchData(): Promise<void> {
      const _commune = await communesAssocieesDelegueesService.getById(id);
      const [_chefLieu, _transfertsChefLieu] = await Promise.all([
        communesService.getById(_commune.codeChefLieu),
        transfertsService.getByCommune(_commune.codeChefLieu),
      ]);

      document.title = _commune.libelle;
      if (_chefLieu == null) {
        return;
      }
      const _transferts = _transfertsChefLieu.filter(
        (t) => t?.affectationsCommunesAssocieesDeleguees?.includes(id) ?? false
      );
      _transferts.sort(competencesUtil.sortTransfertByCompetence);

      setChefLieu(_chefLieu);
      setCommune(_commune);
      setTransferts(_transferts);
      setCompetences(competencesUtil.getCompetencesFromTransferts(_transferts));
    }

    setCommune(undefined);
    setChefLieu(undefined);
    setCompetences([]);
    setTransferts([]);

    void catchErrors(fetchData);
  }, [catchErrors, id]);

  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.only("xs"));

  if (commune == null || chefLieu == null) {
    return <LoadingScreen />;
  }

  return (
    <Container maxWidth={false}>
      <Grid container spacing={2}>
        <PageTitle
          title={commune.libelle}
          icon={<CommuneIcon />}
          subtitle="Commune associée ou déléguée"
        />
        <Grid container size={12}>
          <CommuneLieesSubtitle commune={chefLieu} />
        </Grid>

        <ContactsMetiersInformationBox
          useContactsMetiersParams={{
            $type: "commune-associee-deleguee",
            collectiviteId: id,
            transferts,
            competences,
          }}
        />

        <Grid container size={12}>
          <InformationBox
            hasBackground
            icon={VisibilityIcon}
            articleTitle="Informations générales"
            subtitle={isSmallScreen ? commune.id : `Code INSEE : ${commune.id}`}>
            <Grid container spacing={2} size={12}>
              <Grid size={12}>
                <Typography>Canton : {chefLieu?.canton?.libelle ?? ""}</Typography>
              </Grid>
              <Grid size={12}>
                <Typography>Arrondissement : {chefLieu?.arrondissement?.libelle ?? ""}</Typography>
              </Grid>
              <Grid size={12}>
                <Typography>
                  Date de fusion :{" "}
                  {(commune?.dateFin != null &&
                    dateUtil.format(
                      dateUtil.fromFormatToDate(commune?.dateFin, "yyyy-MM-dd"),
                      "dd/MM/yyyy"
                    )) ??
                    ""}
                </Typography>
              </Grid>
              <Grid size={12}>
                <Typography>
                  Type :
                  {commune.type === TypeCommuneAssocieeDeleguee.DELEGUEE
                    ? " commune déléguée"
                    : " commune associée"}
                </Typography>
              </Grid>
              <Grid size={12}>
                <Typography>
                  Population totale au dernier recensement :{" "}
                  <PopulationSpan
                    population={commune.population.actuelle.populationTotale}
                    annee={commune.population.actuelle.anneeMiseEnVigueur}
                  />
                </Typography>
              </Grid>
            </Grid>
          </InformationBox>
        </Grid>
      </Grid>
    </Container>
  );
}

const CommuneAssocieeDelegueePageWithErrorBoundary = withPageErrorBoundary(
  CommuneAssocieeDelegueePage
);

export default CommuneAssocieeDelegueePageWithErrorBoundary;
